import {
	actionTypes,
	notifyActions,
	conversationReplyActions,
	conversationErrorActions,
} from '../../../controller';
import { ErrorEventEnum } from '../../../lib/enums';
import { SocketTypeEnums, ConversationReplyTypeEnum } from '../../../../lib/enums';
import { catchErrorMessageForSocket } from '../../../../lib/socket-utils';

const {
	SILENCE_EVENT,
} = ErrorEventEnum;
const { QA_FORM } = ConversationReplyTypeEnum;
const {
	notifySocketErrorAction,
} = notifyActions;
const {
	getConversationSilenceAction,
} = conversationErrorActions;
const {
	START_FETCH_CONVERSATION_REPLIES,
	START_FETCH_NEXT_CONVERSATION_REPLIES,
} = actionTypes;
const {
	CONVERSATION_REPLY_TEXT_ACK_CREATED,
	CONVERSATION_REPLY_TEXT_COMMAND_CREATE_ERROR,
	CONVERSATION_REPLIES_ACK_GOT,
	CONVERSATION_REPLIES_COMMAND_GET_ERROR,
	CONVERSATION_REPLY_FORM_TAGS_ACK_CREATED,
	CONVERSATION_REPLY_FORM_TAGS_COMMAND_CREATE_ERROR,
	CONVERSATION_REPLY_FORM_REPLY_ACK_CREATED,
	CONVERSATION_REPLY_FORM_REPLY_COMMAND_CREATE_ERROR,
	CONVERSATION_REPLY_TEXT_ACK_TYPED,
	CONVERSATION_REPLY_EVENT_CREATED,
	CONVERSATION_REPLY_EVENT_UPDATED,
	CONVERSATION_REPLY_QA_OPTIONS_ACK_SELECTED,
	CONVERSATION_REPLY_QA_OPTIONS_COMMAND_SELECT_ERROR,
	CONVERSATION_REPLY_SMART_REPLY_ACK_RESET,
	CONVERSATION_REPLY_CONTACT_CS_ACK_CREATED,
	CONVERSATION_REPLY_CONTACT_CS_COMMAND_CREATE_ERROR,
	CONVERSATION_REPLY_QA_FORM_REPLY_ACK_CREATED,
	CONVERSATION_REPLY_QA_FORM_REPLY_COMMAND_CREATE_ERROR,
	CONVERSATION_REPLY_QA_CHARGE_FORM_REPLY_ACK_CREATED,
	CONVERSATION_REPLY_QA_CHARGE_FORM_REPLY_COMMAND_CREATE_ERROR,
} = SocketTypeEnums;
const {
	createTextConversationReplySuccessAction,
	createTextConversationReplyFailedAction,
	fetchConversationRepliesSuccessAction,
	fetchConversationRepliesFailedAction,
	fetchNextConversationRepliesSuccessAction,
	fetchNextConversationRepliesFailedAction,
	selectConversationReplyTagSuccessAction,
	selectConversationReplyTagFailedAction,
	appendConversationRepliesAction,
	createConversationFormReplySuccessAction,
	createConversationFormReplyFailedAction,
	sendTypingConversationReplySuccessAction,
	receiveConversationReplyAction,
	receiveUpdatedConversationReplyAction,
	selectConversationReplyQAOptionSuccessAction,
	selectConversationReplyQAOptionFailedAction,
	resetConversationSmartReplySuccessAction,
	contactCSConversationSuccessAction,
	contactCSConversationFailedAction,
	createConversationQAFormReplySuccessAction,
	createConversationQAFormReplyFailedAction,
	createConversationQAChargeFormReplySuccessAction,
	createConversationQAChargeFormReplyFailedAction,
} = conversationReplyActions;
const fetchConversationSuccessActionsMap = {
	[START_FETCH_CONVERSATION_REPLIES]: fetchConversationRepliesSuccessAction,
	[START_FETCH_NEXT_CONVERSATION_REPLIES]: fetchNextConversationRepliesSuccessAction,
};
const fetchConversationFailedActionsMap = {
	[START_FETCH_CONVERSATION_REPLIES]: fetchConversationRepliesFailedAction,
	[START_FETCH_NEXT_CONVERSATION_REPLIES]: fetchNextConversationRepliesFailedAction,
};

export function getConversationRepliesReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLIES_ACK_GOT, (payload = {}) => {
		const { messageId, data: { conversationReplies = [], hasNext } = {} } = payload;
		const action = fetchConversationSuccessActionsMap[messageId] ?? fetchConversationRepliesSuccessAction;

		dispatch(action(conversationReplies, hasNext));
	});
}

export function getConversationRepliesErrorReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLIES_COMMAND_GET_ERROR, (payload = {}) => {
		const { messageId, data: error } = payload;
		const action = fetchConversationFailedActionsMap[messageId] ?? fetchConversationRepliesFailedAction;
		const actionObjects = catchErrorMessageForSocket(error, action);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function sendTextConversationReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_TEXT_ACK_CREATED, (payload = {}) => {
		const { data: { conversationReply } = {} } = payload;

		dispatch(createTextConversationReplySuccessAction(conversationReply));
	});
}

export function sendTextConversationErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_TEXT_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const { error: resError } = error;
		const actionObjects = catchErrorMessageForSocket(error, createTextConversationReplyFailedAction);

		if (resError.code === SILENCE_EVENT) {
			const { blockTo, customRateLimitErrorMessage = '' } = resError.options;

			actionObjects.push(getConversationSilenceAction(blockTo, customRateLimitErrorMessage));
		}

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function createConversationTagReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_FORM_TAGS_ACK_CREATED, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(appendConversationRepliesAction(conversationReplies));
		dispatch(selectConversationReplyTagSuccessAction());
	});
}

export function createConversationTagErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_FORM_TAGS_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, selectConversationReplyTagFailedAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function createConversationFormReplyReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_FORM_REPLY_ACK_CREATED, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(createConversationFormReplySuccessAction(conversationReplies));
	});
}

export function createConversationFormReplyErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_FORM_REPLY_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, createConversationFormReplyFailedAction, notifySocketErrorAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function gotTypingConversationReplyReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_TEXT_ACK_TYPED, () => {
		dispatch(sendTypingConversationReplySuccessAction());
	});
}

export function createdConversationReplyEventReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_EVENT_CREATED, ({ data }) => {
		const { conversationId, conversationReplies } = data;

		dispatch(receiveConversationReplyAction(conversationId, conversationReplies));
	});
}

export function updatedConversationReplyEventReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_EVENT_UPDATED, ({ data }) => {
		const { conversationReplies } = data;

		dispatch(receiveUpdatedConversationReplyAction(conversationReplies));
	});
}

export function selectConversationQAOptionReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_QA_OPTIONS_ACK_SELECTED, payload => {
		const { data: { conversationReplies } = {} } = payload;
		const [, ...optionReplies] = conversationReplies;

		dispatch(appendConversationRepliesAction(optionReplies));
		dispatch(selectConversationReplyQAOptionSuccessAction());
	});
}

export function selectConversationQAOptionErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_QA_OPTIONS_COMMAND_SELECT_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, selectConversationReplyQAOptionFailedAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function resetConversationSmartReplyReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_SMART_REPLY_ACK_RESET, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(appendConversationRepliesAction(conversationReplies.filter(({ type }) => type !== QA_FORM)));
		dispatch(resetConversationSmartReplySuccessAction());
	});
}

export function contactCSConversationReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_CONTACT_CS_ACK_CREATED, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(appendConversationRepliesAction(conversationReplies));
		dispatch(contactCSConversationSuccessAction());
	});
}

export function contactCSConversationErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_CONTACT_CS_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, contactCSConversationFailedAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function createConversationQAFormReplyReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_QA_FORM_REPLY_ACK_CREATED, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(createConversationQAFormReplySuccessAction(conversationReplies));
	});
}

export function createConversationQAFormReplyErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_QA_FORM_REPLY_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, createConversationQAFormReplyFailedAction, notifySocketErrorAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}

export function createConversationQAChargeFormReplyReceiver(socket, store) {
	const { dispatch } = store;

	socket.on(CONVERSATION_REPLY_QA_CHARGE_FORM_REPLY_ACK_CREATED, payload => {
		const { data: { conversationReplies } = {} } = payload;

		dispatch(createConversationQAChargeFormReplySuccessAction(conversationReplies));
	});
}

export function createConversationQAChargeFormReplyErrorReceiver(socket, store) {
	socket.on(CONVERSATION_REPLY_QA_CHARGE_FORM_REPLY_COMMAND_CREATE_ERROR, payload => {
		const { dispatch } = store;
		const { data: error } = payload;
		const actionObjects = catchErrorMessageForSocket(error, createConversationQAChargeFormReplyFailedAction, notifySocketErrorAction);

		actionObjects.forEach(actionObject => dispatch(actionObject));
	});
}
